import {messagesService} from "@dv5/api"
import {Comment, getDocumentPartComments, getMarkdownComments, getWidgetComments, useAppSelector} from "@dv5/domain"
import {FC, useState} from "react"
import {Icon, IconName} from "../buttons"
import {CommentDialog} from "./CommentDialog"
import './CommentOverview.less'

interface Props {
    canvasId?: string
    documentId?: string
    sourceItemId: string
    commentsCount: number
    getCommentsMsg?: string
    addCommentMsg?: string
    editCommentMsg?: string
    deleteCommentMsg?: string
    comments?: Comment[]
    onClick?: () => void
    onLoadComments?: (sourceItemId: string) => void
    onSendComment?: (content: string) => void
    onEditComment?: (commentId: string, content: string) => void
    onDeleteComment?: (commentId: string) => void
    variant?: 'overlay' | 'inline'
    className?: string
    markdown?: boolean
}

export const CommentOverview: FC<Props> = ({
                                                canvasId,
                                                documentId,
                                                sourceItemId,
                                                commentsCount,
                                                getCommentsMsg,
                                                addCommentMsg,
                                                editCommentMsg,
                                                deleteCommentMsg,
                                                comments: externalComments,
                                                onClick: externalOnClick,
                                                onLoadComments: externalLoadComments,
                                                onSendComment: externalSendComment,
                                                onEditComment: externalEditComment,
                                                onDeleteComment: externalDeleteComment,
                                                variant = 'overlay',
                                                className,
                                                markdown: markdownProp,
                                            }) => {
    const [showDialog, setShowDialog] = useState(false)
    const widgetComments = useAppSelector(getWidgetComments)
    const partComments = useAppSelector(getDocumentPartComments)
    const globalMarkdownComments = useAppSelector(getMarkdownComments)
    const markdown = markdownProp ?? globalMarkdownComments
    const comments: Comment[] = externalComments ?? (canvasId
        ? (widgetComments[sourceItemId] ?? [])
        : (partComments[sourceItemId] ?? []))

    if (!commentsCount || commentsCount <= 0) {
        return null
    }

    const loadComments = () => {
        if (externalLoadComments) {
            externalLoadComments(sourceItemId)
        } else if (canvasId) {
            messagesService.send(getCommentsMsg ?? '', {canvasId, widgetId: sourceItemId})
        } else {
            messagesService.send(getCommentsMsg ?? '', {documentId, partId: sourceItemId})
        }
    }

    const sendComment = (content: string) => {
        if (externalSendComment) {
            externalSendComment(content)
        } else if (canvasId) {
            messagesService.send(addCommentMsg ?? '', {canvasId, sourceItemId, content})
        } else {
            messagesService.send(addCommentMsg ?? '', {documentId, sourceItemId, content})
        }
        setShowDialog(false)
    }

    const editComment = (commentId: string, content: string) => {
        if (externalEditComment) {
            externalEditComment(commentId, content)
        } else if (canvasId) {
            messagesService.send(editCommentMsg ?? '', {canvasId, sourceItemId, commentId, content})
        } else {
            messagesService.send(editCommentMsg ?? '', {documentId, commentId, content})
        }
    }

    const deleteComment = (commentId: string) => {
        if (externalDeleteComment) {
            externalDeleteComment(commentId)
        } else if (canvasId) {
            messagesService.send(deleteCommentMsg ?? '', {canvasId, sourceItemId, commentId})
        } else {
            messagesService.send(deleteCommentMsg ?? '', {documentId, sourceItemId, commentId})
        }
    }

    const clickComments = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
        event.preventDefault()
        if (externalOnClick) {
            externalOnClick()
        } else {
            setShowDialog(true)
        }
    }

    const rootClass = `comment-overview${variant === 'inline' ? ' inline' : ''}${className ? ` ${className}` : ''}`

    return (
        <>
            <div
                className={rootClass}
                title={`${commentsCount} comment${commentsCount !== 1 ? 's' : ''}`}
                onMouseDown={clickComments}
            >
                <Icon name={IconName.Comment} width="20" height="20"/>
            </div>
            {!externalOnClick && showDialog && (
                <CommentDialog
                    isOpen={showDialog}
                    sourceItemId={sourceItemId}
                    comments={comments}
                    onGetComment={loadComments}
                    onSend={sendComment}
                    onEdit={editComment}
                    onDelete={deleteComment}
                    onClose={() => setShowDialog(false)}
                    markdown={markdown}
                />
            )}
        </>
    )
}
